/**
 * Settings - per-user preferences.
 *
 * Display preferences stay in this browser: they only affect what this
 * browser draws, so there is nothing for the server to know.
 *
 * The alert email opt-in is different and lives on the server. The sender has
 * to be able to read it, and a toggle kept only in localStorage could not
 * stop a single email going out.
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Switch, Snackbar,
  Alert as MuiAlert, Grid,
} from '@mui/material';
import { PageHeader, FilterSelect } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useThemeMode } from '../../context/ThemeContext';
import { getPrefs, setPref } from '../../utils/preferences';
import { getNotificationSettings, updateNotificationSettings } from '../../services/account';

function Settings() {
  const { user } = useAuth();
  const { palette } = useThemeMode();
  const [prefs, setPrefs] = useState(() => getPrefs(user?.email));
  const [toast, setToast] = useState(null);
  const [emailAlerts, setEmailAlerts] = useState(null); // null while loading
  const [emailDigest, setEmailDigest] = useState(null);
  const [emailError, setEmailError] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getNotificationSettings()
      .then((d) => {
        if (cancelled) return;
        setEmailAlerts(!!d.emailAlerts);
        setEmailDigest(!!d.emailDigest);
      })
      .catch(() => { if (!cancelled) setEmailError('Could not load your notification setting.'); });
    return () => { cancelled = true; };
  }, []);

  const save = (key, value) => {
    setPrefs(setPref(user?.email, key, value));
    setToast('Preference saved. It applies the next time you open the page it affects.');
  };

  // Optimistic: the switch answers immediately, and rolls back if the server
  // refuses, so nobody walks away believing they opted out when they did not.
  const saveNotification = async (key, next, labels) => {
    const setter = key === 'emailAlerts' ? setEmailAlerts : setEmailDigest;
    const previous = key === 'emailAlerts' ? emailAlerts : emailDigest;
    setter(next);
    setEmailError('');
    setSavingEmail(true);
    try {
      const d = await updateNotificationSettings({ [key]: next });
      setEmailAlerts(!!d.emailAlerts);
      setEmailDigest(!!d.emailDigest);
      setToast(next ? labels.on : labels.off);
    } catch (err) {
      setter(previous);
      setEmailError('That did not save. Please try again.');
    } finally {
      setSavingEmail(false);
    }
  };

  const helpSx = { fontSize: '0.74rem', color: palette.textMuted, mt: 0.75 };

  return (
    <Box>
      <PageHeader
        eyebrow="Account"
        title="Settings"
        subtitle="Defaults for how Farmgate displays data for you"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Settings' }
        ]}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card>
            <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${palette.divider}` }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Display preferences</Typography>
            </Box>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box>
                <FilterSelect
                  label="Default currency"
                  value={prefs.currency}
                  onChange={(v) => save('currency', v)}
                  options={[
                    { value: 'USD', label: 'USD ($)' },
                    { value: 'NGN', label: 'NGN (₦)' },
                  ]}
                />
                <Typography sx={helpSx}>
                  The Commodities page opens in this currency.
                </Typography>
              </Box>
              <Box>
                <FilterSelect
                  label="Default price unit"
                  value={prefs.priceUnit}
                  onChange={(v) => save('priceUnit', v)}
                  options={[
                    { value: 'bag', label: 'Per bag' },
                    { value: 'kg', label: 'Per kg' },
                  ]}
                />
                <Typography sx={helpSx}>
                  Markets price records open in this unit.
                </Typography>
              </Box>
              <Box>
                <FilterSelect
                  label="Default chart range"
                  value={prefs.chartRange}
                  onChange={(v) => save('chartRange', v)}
                  options={[
                    { value: '1M', label: 'Last month' },
                    { value: '3M', label: 'Last 3 months' },
                    { value: '6M', label: 'Last 6 months' },
                  ]}
                />
                <Typography sx={helpSx}>
                  Dashboard and Currency Analysis charts open on this window.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card>
            <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${palette.divider}` }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Notifications</Typography>
            </Box>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Box>
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: palette.text }}>
                    Email me when an alert triggers
                  </Typography>
                  <Typography sx={helpSx}>
                    At most one email per alert per day, so a price that stays past your
                    threshold cannot fill your inbox. Triggered alerts always appear in the
                    bell either way.
                  </Typography>
                  {emailError && (
                    <Typography sx={{ ...helpSx, color: palette.danger }}>{emailError}</Typography>
                  )}
                </Box>
                <Switch
                  checked={!!emailAlerts}
                  disabled={emailAlerts === null || savingEmail}
                  onChange={(e) => saveNotification('emailAlerts', e.target.checked, {
                    on: 'Alert emails are on.', off: 'Alert emails are off.',
                  })}
                  inputProps={{ 'aria-label': 'Email me when an alert triggers' }}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: palette.accent },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.accent },
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mt: 2.5, pt: 2.5, borderTop: `1px solid ${palette.divider}` }}>
                <Box>
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: palette.text }}>
                    Send me the weekly digest
                  </Typography>
                  <Typography sx={helpSx}>
                    A Monday summary of the commodities you follow: the latest recorded price
                    for each and how it moved. Nothing is sent in a week with no new prices.
                  </Typography>
                </Box>
                <Switch
                  checked={!!emailDigest}
                  disabled={emailDigest === null || savingEmail}
                  onChange={(e) => saveNotification('emailDigest', e.target.checked, {
                    on: 'The weekly digest is on.', off: 'The weekly digest is off.',
                  })}
                  inputProps={{ 'aria-label': 'Send me the weekly digest' }}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: palette.accent },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.accent },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? <MuiAlert severity="success" onClose={() => setToast(null)}>{toast}</MuiAlert> : null}
      </Snackbar>
    </Box>
  );
}

export default Settings;
